import { useNavigate } from 'react-router-dom';
import { useClientTranslation } from '@/lib/core/i18n';
import { useLocaleStore } from '@/lib/core/stores';
import { useApp } from '@/lib/core/providers/appProvider';
import { LinkRouteUrl } from '@/lib/core/utils/links';
import {
  CategoryCardItem,
  firstLink,
  ShopProductItem,
  useShopCategoryPickerItems,
  useShopCollectionProducts,
} from '@/services/platform/data/shop';
import {
  ApiBlockItemModel,
  IApiElementModel,
  SectionFavoriteBrandsElementContentModelContentType,
  SectionHighlightCategoryContentElementContentModelContentType,
  SectionHighlightCollectionContentElementContentModelContentType,
  SectionHighlightCollectionsContentElementContentModelContentType,
  type SectionFavoriteBrandsPropertiesModel,
  type SectionHighlightCategoryContentPropertiesModel,
  type SectionHighlightCollectionContentPropertiesModel,
  type SectionHighlightCollectionsContentPropertiesModel,
} from '@/services/platform/api/model';
import { SectionCarousel } from '../section-carousel';
import { FollowBrandsBanner } from '../brands-banner';
import type { ShopSection } from '../index';

interface ShopSectionBlockDispatcherProps {
  block: ApiBlockItemModel;
  section: ShopSection;
  /** CMS section node id (Sections Repository child) from the page's `sectionPicker`. */
  sectionId?: string;
}

export function ShopSectionBlockDispatcher({ block, section, sectionId }: ShopSectionBlockDispatcherProps) {
  const element = block.content as IApiElementModel | undefined;
  if (!element) return null;

  switch (element.contentType) {
    case SectionHighlightCollectionContentElementContentModelContentType.sectionHighlightCollectionContent:
      return (
        <ShopHighlightCollectionBlock
          properties={
            (element as unknown as { properties?: SectionHighlightCollectionContentPropertiesModel })
              .properties
          }
          section={section}
        />
      );
    case SectionHighlightCategoryContentElementContentModelContentType.sectionHighlightCategoryContent:
      return (
        <ShopHighlightCategoryBlock
          properties={
            (element as unknown as { properties?: SectionHighlightCategoryContentPropertiesModel }).properties
          }
          section={section}
        />
      );
    case SectionHighlightCollectionsContentElementContentModelContentType.sectionHighlightCollectionsContent:
      return (
        <ShopHighlightCollectionsBlock
          properties={
            (element as unknown as { properties?: SectionHighlightCollectionsContentPropertiesModel })
              .properties
          }
          section={section}
        />
      );
    case SectionFavoriteBrandsElementContentModelContentType.sectionFavoriteBrands:
      return (
        <ShopFavoriteBrandsBlock
          properties={
            (element as unknown as { properties?: SectionFavoriteBrandsPropertiesModel }).properties
          }
          section={section}
          sectionId={sectionId}
        />
      );
    default:
      return null;
  }
}

// ── Block wrappers ────────────────────────────────────────────────────────────

function ShopHighlightCollectionBlock({
  properties,
  section,
}: {
  properties?: SectionHighlightCollectionContentPropertiesModel;
  section: ShopSection;
}) {
  const t = useClientTranslation();
  const navigate = useNavigate();
  const lang = useLocaleStore(s => s.locale).split('-')[0];
  const link = firstLink(properties?.link);
  const { items } = useShopCollectionProducts(properties?.collectionPicker, 8);
  if (!properties) return null;

  /*
   * The CMS authors this link as the "Collection List" appScreen for every one of these blocks, and
   * that page cannot know which collection was meant — it reads the id off the last URL segment, and
   * its own URL carries none, so it renders an empty screen. The picked collection is the real
   * destination, so it overrides the authored href, exactly as the discover banner does in
   * `byCategoryCollectionContent`, and lands on the same URL that banner builds.
   *
   * The id comes from the picker, which is available synchronously — a template literal over a value
   * that is still loading yields the *string* `"undefined"` and a clickable `/collection_undefined`.
   * With no collection picked there is nothing better to point at, so the authored href stands.
   */
  const collectionId = properties.collectionPicker?.[0]?.id;
  const href = collectionId ? `/${lang}/shop/${section}/discover/collection_${collectionId}` : link?.href;

  return (
    <SectionCarousel
      title={properties.title ?? ''}
      subtitle={properties.description}
      buttonLabel={link?.label || t('App.Shop.Shop')}
      onButtonClick={href ? () => navigate(href, { state: { section } }) : undefined}
      items={items.map(p => ({
        id: p.id,
        variant: 'product' as const,
        badge: p.badge,
        brand: p.brand,
        name: p.name,
        price: p.price,
        image: p.image,
        onClick: () => navigate(`/${lang}/shop/${section}/discover/product_${p.id}`, { state: { section } }),
      }))}
    />
  );
}

function ShopHighlightCategoryBlock({
  properties,
  section,
}: {
  properties?: SectionHighlightCategoryContentPropertiesModel;
  section: ShopSection;
}) {
  const t = useClientTranslation();
  const navigate = useNavigate();
  const lang = useLocaleStore(s => s.locale).split('-')[0];
  const link = firstLink(properties?.link);
  const { items } = useShopCategoryPickerItems(properties?.categoriesPicker);
  if (!properties) return null;

  // The same CMS block carries either categories or subcategories — pick the
  // fallback button label/route from the picked items' content type.
  const isSubcategories = items.some(item => item.contentType?.toLowerCase().includes('sub'));
  const fallbackLabel = isSubcategories ? t('App.Shop.SeeAllSubcategories') : t('App.Shop.SeeAllCategories');

  return (
    <SectionCarousel
      title={properties.title ?? ''}
      subtitle={properties.description}
      buttonLabel={link?.label || fallbackLabel}
      onButtonClick={
        link?.href
          ? () => navigate(link.href, { state: { section } })
          : () => navigate(`/${lang}/shop/${section}/categories`, { state: { section } })
      }
      items={items.map(item => ({
        id: item.id,
        variant: 'category' as const,
        image: item.image,
        label: item.title,
        onClick: () => {
          const segment = item.contentType?.toLowerCase().includes('sub')
            ? `subcategory_${item.id}`
            : `category_${item.id}`;
          navigate(`/${lang}/shop/${section}/discover/${segment}`, { state: { section } });
        },
      }))}
    />
  );
}

function ShopHighlightCollectionsBlock({
  properties,
  section,
}: {
  properties?: SectionHighlightCollectionsContentPropertiesModel;
  section: ShopSection;
}) {
  const t = useClientTranslation();
  const navigate = useNavigate();
  const lang = useLocaleStore(s => s.locale).split('-')[0];
  const link = firstLink(properties?.link);

  const displayCollectionsOnly = properties?.displayCollectionsOnly;

  const { items }: { items: CategoryCardItem[] | ShopProductItem[] } = displayCollectionsOnly
    ? useShopCategoryPickerItems(properties?.collectionsPicker)
    : useShopCollectionProducts(properties?.collectionsPicker, 8);

  if (!properties) return null;

  if (displayCollectionsOnly) {
    const isSubcategories = (items as CategoryCardItem[]).some(item =>
      item.contentType?.toLowerCase().includes('sub')
    );
    const fallbackLabel = isSubcategories
      ? t('App.Shop.SeeAllSubcategories')
      : t('App.Shop.SeeAllCategories');

    return (
      <SectionCarousel
        title={properties.title ?? ''}
        subtitle={properties.description}
        buttonLabel={link?.label || fallbackLabel}
        onButtonClick={
          link?.href
            ? () => navigate(link.href, { state: { section } })
            : () => navigate(`/${lang}/shop/${section}/categories`, { state: { section } })
        }
        items={(items as CategoryCardItem[]).map(item => ({
          id: item.id,
          variant: 'category' as const,
          image: item?.image ?? item?.imageUrl,
          label: item.title,
          onClick: () => {
            const segment = item.contentType?.toLowerCase().includes('sub')
              ? `subcategory_${item.id}`
              : `category_${item.id}`;
            navigate(`/${lang}/shop/${section}/discover/${segment}`, { state: { section } });
          },
        }))}
      />
    );
  } else {
    return (
      <SectionCarousel
        title={properties.title ?? ''}
        subtitle={properties.description}
        buttonLabel={link?.label || t('App.Shop.Shop')}
        onButtonClick={link?.href ? () => navigate(link.href, { state: { section } }) : undefined}
        items={(items as ShopProductItem[]).map(p => ({
          id: p.id,
          variant: 'product' as const,
          brand: p.brand,
          name: p.name,
          price: p.price,
          image: p.image,
          onClick: () =>
            navigate(`/${lang}/shop/${section}/discover/product_${p.id}`, { state: { section } }),
        }))}
      />
    );
  }
}

/*
 * CMS link from sectionFavoriteBrands points back at the current section page
 * (`/shop-intro/{section}/`), so it is ignored. The banner navigates to the
 * CMS-driven brands page (`/shop/{section}/discover/brands_{sectionId}`, same
 * destination the discover banner builds in `byCategoryBrandContent`), falling
 * back to the app-wide brands route when the page has no `sectionPicker`.
 */
function ShopFavoriteBrandsBlock({
  properties,
  section,
  sectionId,
}: {
  properties?: SectionFavoriteBrandsPropertiesModel;
  section: ShopSection;
  sectionId?: string;
}) {
  const navigate = useNavigate();
  const lang = useLocaleStore(s => s.locale).split('-')[0];
  const { appSettings } = useApp();
  if (!properties) return null;

  const brandsHref = sectionId
    ? `/${lang}/shop/${section}/discover/brands_${sectionId}`
    : LinkRouteUrl(appSettings?.properties?.brands?.route);
  if (!brandsHref) return null;

  return (
    <div className="px-4">
      <FollowBrandsBanner
        title={properties.title}
        description={properties.description}
        onClick={() => navigate(brandsHref, { state: { section } })}
      />
    </div>
  );
}
